import { mapActions } from "vuex";

export const synchronization = {
  methods: {
    ...mapActions({
      getLoggedInUser: "auth/getLoggedInUser",
      getProject: "projects/getProject",
      getPatient: "patients/getPatient",

      createPatient: "patients/createPatient",
      createProject: "projects/createProject",
      updateProject: "projects/updateProject",

      // Products
      createMinerva: "minerva/createMinerva",
      getMinerva: "minerva/get",

      createMoldedSeat: "moldedSeat/createMoldedSeat",
      getMoldedSeat: "moldedSeat/getMoldedSeat",

      createOtn: "otn/createOtn",
      getOtn: "otn/get",

      createLift: "lift/createLift",
      getLift: "lift/get",

      createRetouchingRepair: "retouchingRepair/createRetouchingRepair",
      getRetouchingRepair: "retouchingRepair/getRetouchingRepair",

      createSeat: "seat/createSeat",
      getSeat: "seat/get",

      createPositioningSeat: "positioningSeat/createPositioningSeat",
      getPositioningSeat: "positioningSeat/get",

      createUpperLimb: "upperLimb/createUpperLimb",
      getUpperLimb: "upperLimb/get",

      createLowerLimb: "lowerLimb/createLowerLimb",
      getLowerLimb: "lowerLimb/get",

      createCorset: "corset/createCorset",
      getCorset: "corset/get"
    }),

    // Returns object literal
    products() {
      return {
        NECK_BRACE: {
          table: this.$db.minerva,
          get: this.getMinerva,
          create: this.createMinerva
        },
        MOLDED_SEAT: {
          table: this.$db.molded_seat,
          get: this.getMoldedSeat,
          create: this.createMoldedSeat
        },
        OTN: {
          table: this.$db.otn,
          get: this.getOtn,
          create: this.createOtn
        },
        LIFT: {
          table: this.$db.lift,
          get: this.getLift,
          create: this.createLift
        },
        RETOUCHING_REPAIR: {
          table: this.$db.retouching_repair,
          get: this.getRetouchingRepair,
          create: this.createRetouchingRepair
        },
        SEAT: {
          table: this.$db.seat,
          get: this.getSeat,
          create: this.createSeat
        },
        POSITIONING_SEAT: {
          table: this.$db.positioning_seat,
          get: this.getPositioningSeat,
          create: this.createPositioningSeat
        },
        UPPER_LIMB_ORTHOSIS: {
          table: this.$db.upper_limb,
          get: this.getUpperLimb,
          create: this.createUpperLimb
        },
        LOWER_LIMB_ORTHOSIS: {
          table: this.$db.lower_limb,
          get: this.getLowerLimb,
          create: this.createLowerLimb
        },
        CORSET: {
          table: this.$db.corset,
          get: this.getCorset,
          create: this.createCorset
        }
      };
    },

    // Return false if project dont exist in API
    async checkProject(project_uuid) {
      try {
        return await this.getProject(project_uuid);
      } catch (error) {
        // Return false if response is 404
        if (error.response.status === 404) {
          return false;
        }

        if (
          error.response.data.error &&
          error.response.data.error == "ERR_CANNOT_SEE_PROJECT"
        ) {
          throw error;
        }
        throw error;
      }
    },

    // Synchronizes project/product with API
    async synchronize(project, no_update = false) {
      try {
        // Checks if project exist in API, otherwise create it
        const projectExist = await this.checkProject(project.uuid);

        if (!no_update) {
          if (!projectExist) {
            await this.newProject(project);
          } else {
            await this.existingProject(project, no_update);
          }
        }

        // Retrieves updated project from API
        project = await this.getProject(project.uuid);

        // Add local properties
        project.updated = null;
        project.local_version = null;
        project.server_version = project.product_uuid;
        project.transferable = false;
        // Updated local project
        await this.$db.projects.put(project);
      } catch (error) {
        throw error;
      }
    },

    // Synchronizes existing porject
    async existingProject(project) {
      try {
        const projectRemote = await this.getProject(project.uuid);

        // Checks if project have a local version of a product
        if (project.local_version) {
          // If so, send the product to API
          const product = await this.products()[
            project.product_model
          ].table.get(project.local_version);
          // Gets connected user info
          const user = await this.getLoggedInUser();

          // Add user_id property to product object
          product.user_id = user.id;

          await this.products()[project.product_model].create(product);
        }

        // Remove useless properties
        delete project.local_version;
        delete project.server_version;
        delete project.updated;

        await this.$db.projects.update(projectRemote.uuid, {
          patient_uuid: projectRemote.patient_uuid
        });

        await this.updateProject({
          uuid: project.uuid,
          payload: project
        });

        this.syncMedias(project.uuid);
      } catch (error) {
        console.error("Synchronization.js Method: existingProject: ", error);
        throw error;
      }
    },

    // Synchronizes new project
    async newProject(project) {
      try {
        // Gets local patient
        const patient = await this.$db.patients.get(project.patient_uuid);

        await this.createPatient(patient);
        await this.createProject(project);
        const product = await this.products()[project.product_model].table.get(
          project.product_uuid
        );
        // Gets connected user info
        const user = await this.getLoggedInUser();

        // Add user_id property to product object
        product.user_id = user.id;
        await this.products()[project.product_model].create(product);

        this.syncMedias(project.uuid);
      } catch (error) {
        console.error("Synchronization.js Method: newProject: ", error);
        throw error;
      }
    },

    async syncMedias(project_uuid) {
      const medias = await this.$db.medias.where({ project_uuid }).toArray();

      for (const media of medias) {
        const mediaExist = await this.$store.dispatch("medias/getMedia", {
          project_uuid,
          media_uuid: media.uuid
        });

        if (mediaExist) {
          await this.$store.dispatch("medias/updateSketchMedia", {
            project_uuid,
            media_uuid: media.uuid,
            payload: { croquis: media.croquis }
          });
          continue;
        }

        await this.$store.dispatch("medias/createSketchMedia", {
          project_uuid,
          media_uuid: media.uuid,
          payload: { croquis: media.croquis }
        });

        if (media.image) {
          let data = new FormData();

          data.append("image", media.imageBlob, "image.png");

          await this.$store.dispatch("medias/createImageMedia", {
            project_uuid,
            media_uuid: media.uuid,
            payload: data
          });
        }
      }
    },

    //
    // Gets projects/products
    //

    /**
     * Saves given projects into IndexedDB.
     * Before saving, deletes all unchanged projects from IndexedDB
     * @param {Array} project_uuids_array An array of project uuid
     */
    async savesProjects(project_uuids_array) {
      try {
        /** First step, clean IndexecDB */

        // Gets all local projects
        const localProjects = await this.$db.projects.toArray();

        localProjects.forEach(async project => {
          // Checks if project has changed
          if (project.local_version) {
            return;
          }

          // Deletes given project from IndexedDB
          await this.$db.projects.delete(project.uuid);

          // Deletes the product attached to the given project
          await this.products()[project.product_model].table.delete(
            project.product_uuid
          );
        });

        /** Second step, saves new projects */

        project_uuids_array.forEach(async project_uuid => {
          // Checks if project exist in IndexedDB
          const projectExist = await this.$db.projects.get(project_uuid);
          if (projectExist !== undefined) return;

          // Gets project from API
          const project = await this.getProject(project_uuid);

          // Gets product from API
          const product = await this.products()[project.product_model].get(
            project.product_uuid
          );

          // Save project and product in IndexedDB
          await this.saveProjectProduct(project, product);
        });
      } catch (error) {
        console.warn("FUCKKKK", error);
      }

      // project_uuid_array.forEach(async project_uuid => {
      //   // Deletes all unchanged projects
      //   const project = await this.$db.projects.get(project_uuid);

      //   // Deletes project from IndexedDB
      //   await this.$db.projects.delete(project.uuid);

      //   // Deletes product from IndexedDB
      //   await this.products().products[project.product_model].table.delete(
      //     project.product_uuid
      //   );
      // });
    },

    // Synchronizes projects in IndexedDB
    async synchronizeProjects(projects_array) {
      // Gets products object literals
      const products = this.products();

      // Deletes all projects that are no longer selected
      try {
        // Gets projects primary keys from indexedDB
        const projects = await this.$db.projects.toCollection().keys();

        // Gets deselected projects
        const deselectedProjects = projects.filter(
          item => projects_array.indexOf(item) == -1
        );

        deselectedProjects.forEach(async project_uuid => {
          // Gets project from IndexedDB
          const project = await this.$db.projects.get(project_uuid);

          // Deletes project from IndexedDB
          await this.$db.projects.delete(project.uuid);

          await this.$db.patients.delete(project.patient.uuid);

          // Deletes product from IndexedDB
          await products[project.product_model].table.delete(
            project.product_uuid
          );
        });
      } catch (error) {
        throw error;
      }

      // Iterate through projects
      projects_array.forEach(async project_uuid => {
        // Checks if project exist in IndexedDB
        const projectExist = await this.$db.projects.get(project_uuid);
        if (projectExist !== undefined) return;

        // Gets project from API
        setTimeout(async () => {
          try {
            const project = await this.getProject(project_uuid);

            // Gets product from API
            const product = await products[project.product_model].get(
              project.product_uuid
            );
            await this.saveProjectProduct(project, product);
          } catch (error) {
            throw error;
          }
        }, 10);
      });
    },

    // Synchronize all projects
    async synchronizeAllProjects() {
      // Gets products object literals
      // const products = this.products();

      // Gets all local projects
      const localProjects = await this.$db.projects.toArray();

      // Send projects to API if they are transferable
      localProjects.forEach(localProject => {
        if (localProject.transferable) {
          this.synchronize(localProject);
        }
      });

      // Gets

      // Iterate through projects
      // projects.forEach(async project_uuid => {
      //   // Checks if project exist in IndexedDB
      //   const projectExist = await this.$db.projects.get(project_uuid);
      //   console.log(projectExist);

      //   if (projectExist && projectExist.transferable) {
      //     this.synchronize(projectExist);
      //   }

      //   if (projectExist !== undefined) return;

      //   try {
      //     // Gets project from API
      //     const project = await this.getProject(project_uuid);

      //     // Gets product from API
      //     const product = await products[project.product_model].get(
      //       project.product_uuid
      //     );

      //     // Save project and product in IndexedDB
      //     await this.saveProjectProduct(project, product);
      //   } catch (error) {
      //     throw error;
      //   }
      // });
    },

    // Saves a project and a product in IndexedDB
    async saveProjectProduct(project, product) {
      // Gets products object literals
      const products = this.products();

      // Add versions properties to project object
      project.server_version = project.product_uuid;
      project.local_version = null;

      try {
        // Adds project to IndexedDB
        await this.$db.projects.add(project);

        const patientKey = await this.$db.patients.get(project.patient.uuid);

        if (!patientKey) {
          await this.$db.patients.add(project.patient);
        }

        // Adds product to IndexedDB
        await products[project.product_model].table.add(product);
      } catch (error) {
        throw error;
      }
    }
  }
};
